import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import api from '../utils/axios';
import { LoadingScreen } from './LoadingScreen/LoadingScreen';
import { formatArtifactTitle } from '../utils/parseArtifacts';

interface Artifact {
  id: string;
  project_id: string;
  title: string;
  content: string;
  type: string;
  artifactId?: string;
  version?: string; // Version of the artifact (e.g., "1", "2", "3")
  created_at: string;
  readonly_sharable_link_enabled?: boolean;
}

const ArtifactViewer: React.FC = () => {
  const { projectId, artifactId } = useParams<{ projectId: string; artifactId: string }>();
  const navigate = useNavigate();
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supports both public and private artifact access
    if (!projectId || !artifactId) {
      setError('Invalid URL parameters');
      setLoading(false);
      return;
    }

    fetchArtifact();
  }, [projectId, artifactId]);

  const fetchArtifact = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Include token if available (for private artifacts)
      // Public artifacts with readonly_sharable_link_enabled=true don't require auth
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await api.get(`/projects/${projectId}/artifacts/${artifactId}`, {
        headers
      });
      
      setArtifact(response.data);
    } catch (err: any) {
      console.error('Error fetching artifact:', err);
      if (err.response?.status === 404) {
        setError('Artifact not found or you do not have access to it');
      } else if (err.response?.status === 401) {
        setError('You are not authorized to view this artifact');
      } else {
        setError('Failed to load artifact. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (!artifact) return null;

    // For HTML content, render it directly in full screen
    if (artifact.type === 'text/html') {
      return (
        <iframe
          srcDoc={artifact.content}
          className="w-full h-full border-0"
          title={formatArtifactTitle(artifact.title, artifact.version)}
          sandbox="allow-scripts allow-same-origin"
        />
      );
    }

    // For Markdown content, render with ReactMarkdown
    if (artifact.type === 'text/markdown') {
      return (
        <div className="w-full h-full bg-bg-primary overflow-auto">
          <div className="prose prose-lg max-w-none p-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                code({ className, children, ...props }: any) {
                  const isInline = !className?.includes('language-');
                  return isInline ? (
                    <code className="bg-bg-secondary px-1 py-0.5 rounded text-sm font-mono" {...props}>
                      {children}
                    </code>
                  ) : (
                    <code className="block bg-bg-secondary p-3 rounded text-sm font-mono overflow-x-auto" {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {artifact.content}
            </ReactMarkdown>
          </div>
        </div>
      );
    }

    // For other content types, show as code in full screen
    return (
      <div className="w-full h-full bg-bg-primary overflow-auto">
        <pre className="whitespace-pre-wrap text-sm text-text-primary font-mono p-4 h-full">
          {artifact.content}
        </pre>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-screen">
        <LoadingScreen />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="text-text-error-primary text-lg font-medium mb-2">Error</div>
          <div className="text-text-secondary mb-4">{error}</div>
          <button
            onClick={() => navigate(`/chat/${projectId}`)}
            className="px-4 py-2 bg-bg-brand-solid text-text-primary-on-brand rounded hover:bg-bg-brand-solid-hover transition-colors"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="h-screen w-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="text-text-secondary text-lg">Artifact not found</div>
          <button
            onClick={() => navigate(`/chat/${projectId}`)}
            className="mt-4 px-4 py-2 bg-bg-brand-solid text-text-primary-on-brand rounded hover:bg-bg-brand-solid-hover transition-colors"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-bg-primary">
      {renderContent()}
    </div>
  );
};

export default ArtifactViewer;
